import * as React from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useFormikContext } from 'formik';

import { DeleteOutlined, FilePdfOutlined } from '@ant-design/icons';
import Alert from 'antd/lib/alert';
import Button from 'antd/lib/button';
import { Col, Row } from 'antd/lib/grid';

import { ApplicationState } from '@app/store';
import { useCrematory } from '@app/hooks/Crematory/useCrematory';
import { Card } from '@app/components/UI/Cards/Card';
import { ViewMode } from '@app/components/Utils/Utils';
import { activePermission, Permission } from '@app/objects/Permission';
import { FormValues } from '@app/components/Pages/ClinicPetEditor/types';
import { ResourceButton } from '@app/components/UI/Buttons/ResourceButton';
import { PetDetails } from '@app/components/Pages/ClinicPetEditor/ClinicPetsComponents/PetDetails';
import { getClinicPetEditPath, petListPath } from '@app/components/Pages/ClinicPetEditor/ClinicApi';
import { ProductList } from '@app/components/Pages/ClinicPetEditor/ClinicPetsComponents/ProductList';
import { UrnList } from '@app/components/Pages/ClinicPetEditor/ClinicPetsComponents/UrnList/UrnList';
import { EditorHeader } from '@app/components/UI/Header/PageHeaders/EditorHeader';
import { handleDelete } from '@app/components/UI/Table/services';
import { NotesDetails } from '@app/components/Pages/ClinicPetEditor/ClinicPetsComponents/NotesDetails';
import { OwnerDetails } from '@app/components/Pages/ClinicPetEditor/ClinicPetsComponents/OwnerDetails';
import { DocumentsList } from '@app/components/Pages/ClinicPetEditor/ClinicPetsComponents/DocumentsList';
import { CommunalWarning } from '@app/components/Pages/ClinicPetEditor/ClinicPetsComponents/CommunalWarning';
import { DeliveryDetails } from '@app/components/Pages/ClinicPetEditor/ClinicPetsComponents/DeliveryDetails';
import { CremationDetails } from '@app/components/Pages/ClinicPetEditor/ClinicPetsComponents/CremationDetails';
import {
	SpecialServices,
} from '@app/components/Pages/ClinicPetEditor/ClinicPetsComponents/SpecialServices/SpecialServices';
import {
	UrnsProvider,
} from '@app/components/Pages/ClinicPetEditor/ClinicPetsComponents/StoreEntriesModal/UrnProviders';
import { PriceType } from '@app/objects/Price';
import { useArchiveAccess } from '@app/hooks/Crematory/useArchiveAccess';
import { useDeliveryManager } from '@app/hooks/Delivery/useDeliveryManager';

interface ClinicPetFormProps {
	mode: ViewMode;
	petId: number;
	hasImportantChanges: boolean;
}

const resourceButtons = (id: number) => {
	if (id < 0) return null;

	return (
		<ResourceButton
			className="ant-btn btn btn-pdf"
			request={`/get-pet-tag?petId=${id}`}
			icon={<FilePdfOutlined />}
		>
			Print tag
		</ResourceButton>
	);
};

const DeleteCard: React.FC<Omit<ClinicPetFormProps, 'hasImportantChanges'>> = ({ mode, petId }) => {
	const history = useHistory();
	const { t } = useTranslation();
	const user = useSelector((state: ApplicationState) => state.login.user);

	if (mode !== ViewMode.edit || petId === -1 || !activePermission(user, Permission.DeletePet)) return null;

	return (
		<Card
			name="Danger"
			fullWidth

			viewContent={(
				<>
					<div style={{ gridColumn: 1 / -2 }}>Pet removal button</div>
					<div className="site-header__buttons" style={{ gridColumn: 3, justifySelf: 'end' }}>
						<Button
							title="Delete"
							onClick={() =>
								handleDelete({
									t,
									itemName: 'Pet',
									deleteEndpoint: 'deletePet',
									reload: () => history.replace('/'),
									id: petId,
								})}
							danger
							icon={<DeleteOutlined />}
						>
							Delete
						</Button>
					</div>
				</>
			)}
		/>
	);
};

function useWithDeliveryAddress(): boolean {
	const manager = useDeliveryManager();
	const { values } = useFormikContext<FormValues>();
	const value = (manager.type === 'modern' ? values.deliveryTypeId : values.deliveryType) ?? null;

	return manager.isMail(value) || manager.isHomeDropOff(value) || manager.isVetDropOff(value);
}

export const ClinicPetForm: React.FC<ClinicPetFormProps> = (props: ClinicPetFormProps) => {
	const { values } = useFormikContext<FormValues>();
	const user = useSelector((state: ApplicationState) => state.login.user);
	const activeCrematoryId = user?.activeCrematoryId ?? -1;
	const crematory = useCrematory();
	const showDeliveryAddress = useWithDeliveryAddress();
	const access = useArchiveAccess();

	return (
		<>
			<div className="page-container__header">
				<div className="page-container__header_clinic">
					<EditorHeader
						name="Pet"
						editPath={getClinicPetEditPath(values.id)}
						backPath={petListPath}
						viewButtons={(access.canAccess && values.isEditable && activePermission(user, Permission.UpdatePet) ? undefined : '')}
						extraButtons={resourceButtons(props.petId)}
					/>
				</div>
				{props.hasImportantChanges && (
					<Alert
						message="Important changes have been made for this pet. We recommend reprinting the tag."
						type="warning"
						style={{ marginBottom: '20px' }}
					/>
				)}
			</div>
			<div className="page-container__content">
				<CommunalWarning mode={props.mode} />

				<Row gutter={[30, 16]}>
					<Col xs={24} md={12}>
						<CremationDetails mode={props.mode} crematory={crematory} />
					</Col>
					<Col xs={24} md={12}>
						<PetDetails mode={props.mode} crematory={crematory} />
					</Col>
					<Col xs={24} md={12}>
						<OwnerDetails mode={props.mode} crematoryId={activeCrematoryId} />
					</Col>
					{
						showDeliveryAddress
							? (
								<Col xs={24} md={12}>
									<DeliveryDetails mode={props.mode} />
								</Col>
							) : null
					}
					<Col xs={24} xxl={12}>
						<SpecialServices mode={props.mode} crematoryId={activeCrematoryId} />
					</Col>
					<UrnsProvider
						crematoryId={activeCrematoryId}
						serviceType={values.serviceType}
						priceType={PriceType.Wholesale}
					>
						<Col xs={24} xxl={12}>
							<UrnList
								mode={props.mode}
								crematoryId={activeCrematoryId}
							/>
						</Col>
					</UrnsProvider>
					<Col xs={24} xxl={12}>
						<ProductList mode={props.mode} crematoryId={activeCrematoryId} />
					</Col>
					<Col xs={24} xxl={12}>
						<NotesDetails mode={props.mode} />
					</Col>
					{
						crematory?.clinicCanUploadFiles
							? (
								<Col xs={24} xxl={12}>
									<DocumentsList
										mode={props.mode}
										crematoryId={activeCrematoryId}
									/>
								</Col>
							) : null
					}
					<Col xs={24}>
						<DeleteCard mode={props.mode} petId={props.petId} />
					</Col>
				</Row>
			</div>
		</>
	);
};
